import { Box } from "@mui/material";
import type { CSSProperties, ReactNode } from "react";
import {
  SITE_COLOR,
  SITE_MEDIA,
  SITE_SHADOW,
  SITE_TYPE,
  SITE_VAR,
} from "src/domains/shared/constants/styles/site";
import { revealRef } from "./motion/reveal";
import { SiteTitle } from "./SiteText";

const ROW_STYLES = {
  list: { marginTop: SITE_VAR.block },
  listAfterHead: { marginTop: 0 },
  row: {
    display: "grid",
    gridTemplateColumns: "180px minmax(0,1fr) minmax(0,1.25fr)",
    gap: "clamp(28px,4.2vw,64px)",
    paddingBlock: "clamp(44px,5.6vw,80px)",
    borderTop: `1px solid ${SITE_COLOR.line}`,
    alignItems: "start",
    [SITE_MEDIA.wide]: { gridTemplateColumns: "minmax(0,1fr) minmax(0,1.2fr)" },
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "1fr", gap: "24px" },
    // 가로 터치 화면: 글 아래 전폭 그림은 화면 두 개 높이다 — 글 | 그림 두 칸(머리말은 위 한 줄)
    [SITE_MEDIA.landscapeTouch]: {
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1.2fr)",
      gap: "clamp(20px,3vw,32px)",
      alignItems: "center",
    },
  },
  kicker: {
    fontSize: "16px",
    fontWeight: 600,
    color: SITE_COLOR.ink3,
    paddingTop: "6px",
    [SITE_MEDIA.wide]: { gridColumn: "1/-1", padding: 0 },
  },
  text: { paddingTop: "2px" },
  title: { ...SITE_TYPE.h3, marginBottom: "18px" },
  body: SITE_TYPE.body,
  /** 흰 화면 그림이 흰 바탕에 묻히지 않게 옅은 테두리선 + 그림자(시안 `.shot`) */
  shot: {
    position: "relative",
    background: SITE_COLOR.panel,
    borderRadius: SITE_VAR.radiusImage,
    overflow: "hidden",
    isolation: "isolate",
    boxShadow: SITE_SHADOW.frame,
    "& > img, & > video": {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    },
  },
  /** 앱 화면 캡처(시안 `.ashot`): 흰 바탕 + 안쪽 테두리선 */
  appShot: {
    background: "#fff",
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,.06)",
    "& > img": { objectPosition: "var(--op,50% 50%)" },
  },
} as const;

export type FeatureShotRatio = "16/10" | "4/3";

export interface FeatureRowProps {
  kicker: string;
  title: string;
  text: string;
  media: ReactNode;
  ratio?: FeatureShotRatio;
  /** 앱 화면 캡처면 흰 바탕·안쪽 테두리선 */
  appShot?: boolean;
}

/** 기능 행(시안 `.row`): 작은 머리말 | 제목·설명 | 그림. */
export function FeatureRow({
  kicker,
  title,
  text,
  media,
  ratio = "16/10",
  appShot = true,
}: FeatureRowProps) {
  return (
    <Box component="article" sx={ROW_STYLES.row}>
      <Box
        component="p"
        ref={revealRef}
        data-reveal="up"
        sx={ROW_STYLES.kicker}
      >
        {kicker}
      </Box>
      <Box
        ref={revealRef}
        data-reveal="up"
        style={{ "--i": 1 } as CSSProperties}
        sx={ROW_STYLES.text}
      >
        <Box component="h3" sx={ROW_STYLES.title}>
          <SiteTitle text={title} />
        </Box>
        <Box component="p" sx={ROW_STYLES.body}>
          {text}
        </Box>
      </Box>
      <Box
        component="figure"
        ref={revealRef}
        data-reveal="img"
        style={{ "--i": 2 } as CSSProperties}
        sx={[
          ROW_STYLES.shot,
          { aspectRatio: ratio },
          appShot && ROW_STYLES.appShot,
        ]}
      >
        {media}
      </Box>
    </Box>
  );
}

/** 기능 행 묶음(시안 `.rows`) — 구역 머리말 바로 뒤면 위 여백 없이 */
export function FeatureRows({
  children,
  afterHead = false,
}: {
  children: ReactNode;
  afterHead?: boolean;
}) {
  return (
    <Box sx={afterHead ? ROW_STYLES.listAfterHead : ROW_STYLES.list}>
      {children}
    </Box>
  );
}
